import Button from '@ui/Button';
import { BidDocument } from '@models/bid';
import { ProductDocument } from 'models/product';
import { formatCurrency, formatTimestamp } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import Link from 'next/link';
import React from 'react';
import { JamCrownIcon } from './AuctionStatus';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';

const Col = ({ text }: { text: string }) => (
  <div className="flex grow gap-[0.8rem] px-[1rem] py-[1.2rem]">
    <span
      className="text-[1.3rem] font-semibold text-[#4F4D4B]"
      suppressHydrationWarning
    >
      {text}
    </span>
  </div>
);

const THead = ({ hasAdminRole = false }: { hasAdminRole?: boolean }) => (
  <div className="flex w-full items-center border-t-[1px] border-[#eeeeee] bg-white">
    <Col text="Bidder" />
    <Col text="Amount" />
    {hasAdminRole && <Col text="Max Bid" />}
    <Col text="Time" />
  </div>
);

const BidCol = ({
  icon,
  text,
  leading,
  bidderUid,
  // hasAuctionRole,
  hasAdminRole,
  productId,
  bidId,
}: {
  icon?: React.ReactNode | null;
  leading?: boolean;
  text: string;
  bidderUid?: string;
  hasAuctionRole?: boolean;
  hasAdminRole?: boolean;
  productId: string;
  bidId?: string;
}) => {
  const { openChatModalWithRecipientAndProductId } = useChat();
  return (
    <div className="flex w-full grow gap-[0.8rem] px-[1rem] py-[1.2rem]">
      {icon}
      <span
        className={`text-[1.3rem] font-semibold ${
          leading ? 'text-brand-secondary' : 'text-black'
        }`}
        suppressHydrationWarning
      >
        {bidderUid && hasAdminRole ? (
          <div className="flex items-start gap-2">
            <Link
              target={'_blank'}
              className="underline"
              href={`https://console.firebase.google.com/project/mxlocker-b1d4a/firestore/data/~2Fusers~2F${bidderUid}`}
            >
              {text}
            </Link>
            <Button
              height="smaller"
              width="smaller"
              onClick={() => {
                openChatModalWithRecipientAndProductId({
                  recipientUid: bidderUid,
                  productId,
                });
              }}
              text={'Message'}
            />
          </div>
        ) : hasAdminRole && bidId ? (
          <Link
            target={'_blank'}
            className="underline"
            href={`https://console.firebase.google.com/project/mxlocker-b1d4a/firestore/data/~2Fbids~2F${bidId}`}
          >
            {text}
          </Link>
        ) : (
          text
        )}
      </span>
    </div>
  );
};

const TBid = ({
  bidder,
  amount,
  maxBid,
  bidId,
  bidderUid,
  productId,
  time,
  leading = false,
  hasAdminRole = false,
  hasAuctionRole = false,
}: {
  leading?: boolean;
  bidder: string;
  amount: string;
  maxBid?: string;
  bidId?: string;
  bidderUid?: string;
  productId: string;
  time: string;
  hasAdminRole?: boolean;
  hasAuctionRole?: boolean;
}) => (
  <div
    className={`border-color-[#eeeeee] z-1 flex w-full items-center border-b-[1px]
    ${
      leading
        ? 'bg-brand-secondary-light'
        : bidder === 'You'
        ? 'bg-brand-primary-lighter'
        : 'bg-white'
    }
      `}
  >
    <BidCol
      icon={leading ? <JamCrownIcon /> : null}
      text={bidder}
      leading={leading}
      productId={productId}
      bidderUid={
        (hasAdminRole || hasAuctionRole) && bidderUid ? bidderUid : undefined
      }
      hasAuctionRole={hasAuctionRole}
      hasAdminRole={hasAdminRole}
    />
    <BidCol text={amount} productId={productId} />
    {hasAdminRole && (
      <BidCol
        text={maxBid ?? 'N/A'}
        productId={productId}
        hasAuctionRole={hasAuctionRole}
        hasAdminRole={hasAdminRole}
        bidId={bidId}
      />
    )}
    <BidCol text={time} productId={productId} />
  </div>
);

interface Props {
  product: ProductDocument;
}
export const BidHistoryTable = ({ product }: Props) => {
  const { userDoc } = useAuth();
  const { realTimeBids: bids } = useRealTimeBids(product.id, userDoc?.uid);

  const getUsername = (bid: BidDocument) => {
    if (bid.uid === userDoc?.uid) {
      return 'You';
    }
    const firstInitial = bid.username.charAt(0);
    const lastInitial = bid.username.charAt(bid.username.length - 1);
    return `${firstInitial}****${lastInitial}`;
  };
  return (
    <div className="flex flex-col gap-[0.8rem]">
      <span className="text-[1.5rem] font-semibold">Bid History</span>
      <div className="isolate flex max-h-[30rem] flex-col">
        <THead hasAdminRole={userDoc?.roles?.admin} />
        <div className="max-h-[30rem] overflow-y-auto">
          {bids.map((bid, i) => (
            <TBid
              key={bid.uid.slice(0, 5) + '-' + bid.bid_price + '-' + i}
              bidder={getUsername(bid)}
              amount={formatCurrency(bid.bid_price)}
              bidId={bid.id}
              bidderUid={bid.uid}
              productId={product.id}
              maxBid={bid.max_bid ? formatCurrency(bid.max_bid) : undefined}
              hasAdminRole={userDoc?.roles?.admin}
              hasAuctionRole={userDoc?.roles?.auctions}
              time={formatTimestamp(bid.created)}
              leading={i === 0}
            />
          ))}
          <TBid
            key="starting-bid"
            bidder="Starting Bid"
            productId={product.id}
            amount={formatCurrency(product.og_price)}
            time={formatTimestamp(product.start_time ?? product.created)}
          />
        </div>
      </div>
    </div>
  );
};
